/**
 * AGC: Modal Hook - Provides alert() and confirm() replacements for Base App
 * AGC: Works in Base App, Farcaster, and desktop browsers
 * @author AGC (AI-Generated Code) with Cursor
 */

import { useState, useCallback, useRef } from 'react'
import { AlertModal } from '../components/modals/AlertModal'
import { ConfirmModal } from '../components/modals/ConfirmModal'

interface AlertOptions {
  type?: 'success' | 'error' | 'info' | 'warning'
  title?: string
}

interface ConfirmOptions {
  title?: string
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger'
}

export const useModal = () => {
  const [alertState, setAlertState] = useState<{
    isOpen: boolean
    message: string
    type: 'success' | 'error' | 'info' | 'warning'
    title?: string
  }>({
    isOpen: false,
    message: '',
    type: 'info'
  })

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean
    message: string
    title?: string
    confirmText?: string
    cancelText?: string
    confirmVariant?: 'primary' | 'danger'
  }>({
    isOpen: false,
    message: ''
  })

  // AGC: Use refs to store resolve functions
  const alertResolveRef = useRef<(() => void) | null>(null)
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null)

  // AGC: Alert function - returns a promise that resolves when user closes
  const alert = useCallback((message: string, options?: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      alertResolveRef.current = resolve
      setAlertState({
        isOpen: true,
        message,
        type: options?.type || 'info',
        title: options?.title
      })
    })
  }, [])

  // AGC: Confirm function - returns a promise that resolves with true/false
  const confirm = useCallback((message: string, options?: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve
      setConfirmState({
        isOpen: true,
        message,
        title: options?.title,
        confirmText: options?.confirmText,
        cancelText: options?.cancelText,
        confirmVariant: options?.confirmVariant
      })
    })
  }, [])

  // AGC: Close alert handler
  const closeAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }))
    if (alertResolveRef.current) {
      alertResolveRef.current()
      alertResolveRef.current = null
    }
  }, [])

  // AGC: Confirm handler
  const handleConfirm = useCallback(() => {
    setConfirmState(prev => ({ ...prev, isOpen: false }))
    if (confirmResolveRef.current) {
      confirmResolveRef.current(true)
      confirmResolveRef.current = null
    }
  }, [])

  // AGC: Cancel handler
  const handleCancel = useCallback(() => {
    setConfirmState(prev => ({ ...prev, isOpen: false }))
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false)
      confirmResolveRef.current = null
    }
  }, [])

  // AGC: Modal components
  const Modals = (
    <>
      <AlertModal
        isOpen={alertState.isOpen}
        message={alertState.message}
        type={alertState.type}
        title={alertState.title}
        onClose={closeAlert}
      />
      <ConfirmModal
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        title={confirmState.title}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmVariant={confirmState.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  )

  return {
    alert,
    confirm,
    Modals
  }
}

